"use client";

import { useEffect } from "react";

/**
 * Prevents React DevTools visual editor from popping up when selecting components
 */
export function DisableDevTools() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Prevent component inspection by blocking the visual editor trigger
    const preventVisualEditor = (e: MouseEvent) => {
      // React DevTools visual editor is typically triggered by Ctrl/Cmd + Shift + Click
      // or by certain click patterns that DevTools intercepts
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    // Also prevent mousemove events that might trigger component highlighting
    const preventHighlighting = (e: MouseEvent) => {
      // Prevent DevTools from highlighting components on hover when modifier keys are held
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        e.stopImmediatePropagation();
      }
    };

    // Use capture phase to intercept before React DevTools can handle it
    document.addEventListener("click", preventVisualEditor, { capture: true, passive: false });
    document.addEventListener("mousedown", preventVisualEditor, { capture: true, passive: false });
    document.addEventListener("mousemove", preventHighlighting, { capture: true, passive: false });

    // Also try to disable the React DevTools component inspector directly
    if ((window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__) {
      const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
      
      // Disable the renderer that DevTools uses for component inspection
      if (hook.renderers) {
        const originalRenderers = hook.renderers;
        hook.renderers = new Map();
        
        // Restore after a short delay to allow DevTools to initialize, but prevent visual editor
        setTimeout(() => {
          hook.renderers = originalRenderers;
        }, 100);
      }
    }

    return () => {
      document.removeEventListener("click", preventVisualEditor, { capture: true } as any);
      document.removeEventListener("mousedown", preventVisualEditor, { capture: true } as any);
      document.removeEventListener("mousemove", preventHighlighting, { capture: true } as any);
    };
  }, []);

  return null;
}
